export type MaturityLevel = {
  level: number;
  name: string;
  description: string;
};

export type Control = {
  id: string;
  name: string;
  description: string;
  maturityCriteria: Record<number, string>;
  assessmentQuestion?: string;
  evidence?: string;
  mappedStandards: string[];
};

export type SubDomain = {
  id: string;
  name: string;
  description: string;
  controls: Control[];
};

export type Domain = {
  id: string;
  name: string;
  description: string;
  objective?: string;
  subDomains: SubDomain[];
};

export type Framework = {
  title: string;
  version: string;
  maturityLevels: MaturityLevel[];
  domains: Domain[];
};

// Cisco technology footprint — repurposes the "use case" mechanism (adaptive
// threat/question filtering keyed by this id) for "which infrastructure do
// you run" instead of "which AI adoption pattern applies".
export type UseCaseId =
  | "nexus_dc"
  | "meraki"
  | "catalyst_sdwan"
  | "catalyst_switching"
  | "cisco_firewall"
  | "collaboration"
  | "wifi"
  // Free-text "Other / non-Cisco infrastructure" — scored with a conservative
  // default profile, same role as the old "other_custom".
  | "other_non_cisco";

// ---------- Business entities / Cisco product-architecture taxonomy ----------
// A finer-grained sub-layer UNDER the 8 UseCaseIds above: specific Cisco
// product/architecture line items (e.g. "NX9K", "Meraki Wireless") a customer
// can upload (from an export like the Cisco Ready categories workbook) or
// manually pick. Every item optionally rolls up to a UseCaseId so selecting
// entities can auto-select the technology-footprint domain they belong to;
// items with no clean fit (e.g. IAM, SP-carrier gear) roll up to `null` and
// are display-only — captured in the report, never scored. See
// modules/entities/catalog.ts for the actual catalog data.

export type EntityCategoryId = string;
export type EntityItemId = string;

export type EntityCategory = {
  id: EntityCategoryId;
  label: string;
  /** Rollup target when a member item has no `useCaseIdOverride`. `null` =
   *  captured for display only, never auto-selects or scores a UseCase. */
  defaultUseCaseId: UseCaseId | null;
};

export type EntityItem = {
  id: EntityItemId;
  label: string;
  categoryId: EntityCategoryId;
  /** Match variants for the Excel-import fuzzy matcher (exact/alias/fuzzy). */
  aliases: string[];
  /** Overrides the category's `defaultUseCaseId` for this one item — e.g.
   *  every Meraki-branded item rolls up to `meraki` regardless of which
   *  Cisco-taxonomy category it was filed under upstream. */
  useCaseIdOverride?: UseCaseId | null;
  /** Flags a legacy/EOL-prone product line for display-only "informational"
   *  treatment in the report — NOT wired into scoring (see build plan). */
  eolRisk?: boolean;
};

export type EntityMatchConfidence = "exact" | "alias" | "fuzzy" | "manual";

/** A committed (post-confirmation) entity selection, stored on the assessment. */
export type EntityImportKind = "excel" | "manual" | "api";

export type EntitySelection = {
  itemId: EntityItemId;
  source: EntityImportKind;
  confidence: EntityMatchConfidence;
  rawLabel?: string; // original text as seen in an uploaded source, for audit
  addedAt: string; // ISO timestamp
};

export type MatchedEntity = {
  itemId: EntityItemId;
  confidence: EntityMatchConfidence;
  rawLabel: string;
};

export type UnmatchedEntityRow = {
  rawLabel: string;
  rawCategory?: string;
  suggestions: Array<{ itemId: EntityItemId; score: number }>;
};

/** The one shape every entity source (Excel upload today; manual picker;
 *  a future live API) must produce — downstream code (store, review UI,
 *  report) never needs to know which source produced it. */
export type EntityImportResult = {
  matched: MatchedEntity[];
  unmatched: UnmatchedEntityRow[];
  sourceMeta: { kind: EntityImportKind; fileName?: string; importedAt: string };
};

// Infrastructure profile — repurposes the "environment" mechanism (per
// env-answer likelihood/impact modifiers) for organizational/infrastructure
// context instead of AI deployment context. The assessment is scoped to the
// customer's whole environment, so no dimension here modulates the FAIR
// impact axis any more (see IMPACT_ENV_KEYS in taxonomy/weights.ts) — every
// dimension left is a likelihood (attack-surface) modifier.
export type EnvDimensionId =
  | "network_complexity"
  | "cloud_onprem_mix"
  | "ai_agents_deployed";

/** Org-wide SOC/incident-response autonomy ladder, 1..5. Optional — an
 *  unanswered assessment defaults to 3 (neutral) in scoring. */
export type AutonomyLevel = 1 | 2 | 3 | 4 | 5;

export type EnvAnswers = Partial<Record<EnvDimensionId, string>>;

export type MaturityAnswers = Record<string, number>; // controlId -> 1..5

// Cisco Technology Adoption — per-platform answers about how maturely the
// customer has adopted Cisco's own management/visibility tooling (Cisco IQ,
// Cloud Control, Nexus Dashboard, Meraki, etc). Keyed platformId -> fieldId ->
// value; field kinds are boolean (yes/no), select (option value string), or
// text (free-form comments). See taxonomy/tech-adoption.ts for the catalog.
export type TechAdoptionAnswers = Record<string, Record<string, string | boolean>>;

export type AssessmentInput = {
  useCase?: UseCaseId;
  env: EnvAnswers;
  /** Org-wide autonomy level (1..5). Default 3 if omitted. */
  autonomyLevel?: AutonomyLevel;
  maturity: MaturityAnswers;
};

// A threat's category is a use-case-style grouping (LLM / Agentic / Operational)
// that is editable + extensible from /admin. It is deliberately distinct from the
// external framework taxonomies (OWASP LLM Top 10, OWASP Agentic, Cisco AI
// Taxonomy) that threats are separately *mapped* to. `category` is a free string
// keyed to a ThreatCategoryDef.id; the seed list lives in
// modules/mappings/threat-categories.ts and is resolved via the content module.
export type ThreatCategory = string;

// An editable threat-category definition. `operational: true` marks a category
// whose threats are NOT required to map to an external framework (the old
// hardcoded `category === "OPERATIONAL"` rule).
export type ThreatCategoryDef = {
  id: string;
  label: string;
  order?: number;
  operational?: boolean;
};

export type Threat = {
  id: string;
  name: string;
  category: ThreatCategory;
  shortLabel: string;
  description: string;
  primaryControlIds: string[];
  secondaryControlIds: string[];
};

export type CiscoProduct = {
  id: string;
  name: string;
  shortName: string;
  url: string;
};

export type CiscoProductMapping = {
  product: CiscoProduct;
  rationale: string;
  // V3.4 (#5): longer-form "how this product closes the gap" shown in the
  // focused-remediation "Read more" disclosure. Optional — degrades to just
  // the short rationale when absent.
  howItHelps?: string;
};

export type ThreatExposure = {
  threatId: string;
  exposure: number; // 0..1 — FAIR risk = likelihood × impact
  likelihood: number; // 0..1 — loss-event frequency × vulnerability (post-maturity)
  impact: number; // 0..1 — loss magnitude (not reduced by maturity)
  baseWeight: number;
  maturityFactor: number; // 1 - normalized maturity
  contributingControlIds: string[];
  severity: "Critical" | "High" | "Medium" | "Low";
  /**
   * V4.2 (FINDINGS.md F-07). How far THIS deployment elevates the threat above
   * a neutral deployment of the same use case at the same maturity: the ratio
   * of actual exposure to exposure recomputed with every environment answer at
   * its neutral value and autonomy at 3. Base weight, threat-impact prior and
   * maturity factor all cancel, so what remains is purely the amplification the
   * organisation's own configuration contributes. 1.0 = typical; >1 = this
   * org's choices make it worse than average; <1 = better.
   *
   * Ranking by absolute exposure alone made the top five a restatement of the
   * impact table — sensitive_disclosure appeared in 41 of 50 corpus scenarios
   * and model_extraction in none, almost regardless of the deployment.
   */
  elevation?: number;
};

export type DomainScore = {
  domainId: string;
  current: number; // 1..5
  target: number;
};

export type Recommendation = {
  controlId: string;
  controlName: string;
  domainId: string;
  current: number;
  target: number;
  expectedRiskReduction: number; // 0..1 raw-exposure delta
  /**
   * V4.2 — the same improvement expressed in the points the user actually sees.
   * A delta cannot be pushed through the (nonlinear) 0–100 calibration, so both
   * endpoints are carried through the counterfactual and differenced on the
   * calibrated scale instead: `toScore100(before) - toScore100(after)`. This is
   * the number the recommendation list is sorted by, so the list is ordered by
   * the figure printed on it.
   */
  expectedScoreDrop: number; // 0..100 points
  mappedStandards: string[];
  rationale: string;
  productMappings?: CiscoProductMapping[];
  /** "What do I do next" framed for two distinct report audiences. Optional —
   *  absent for a control that hasn't been authored yet (see modules/role-actions). */
  roleActions?: RoleActions;
};

/** Per-control "next step" copy for the two audiences a report reaches:
 *  a Portfolio Manager (budget/roadmap/vendor framing) and a Security
 *  Engineer (technical/control-implementation framing). Authored per control
 *  in content/report/role-actions/*, with a generated fallback for any
 *  control not yet authored — see modules/role-actions. */
export type RoleActions = {
  portfolioManager: string;
  securityEngineer: string;
};

export type RiskProfile = {
  overall: number; // 0..1
  overallLabel: "Critical" | "High" | "Medium" | "Low";
  topThreats: ThreatExposure[];
  threatExposures: ThreatExposure[];
  domainScores: DomainScore[];
  recommendations: Recommendation[];
  generatedAt: string;
};

// ---------- V2: multi-use-case ----------

export type UseCaseSelection = {
  id: UseCaseId;
  env: EnvAnswers;
  customDescription?: string; // V3: free-text label for id="other_custom"
};

export type Stakeholder = {
  name: string;
  role: string;
  email?: string;
};

// V8 — internal people accountable for AI risk inside the org. Distinct from
// `stakeholders` (the report's "Prepared for" recipients).
export type InternalStakeholder = {
  name: string;
  role: string;
  email: string;
  department: string;
};

// V3: organization branding for reports. Logo is held in-memory only as a
// data URL (never persisted to disk on the server).
export type Branding = {
  orgName: string;
  /** Salesforce AccountId when the org name was picked via the `sf_account` lookup. */
  orgAccountId?: string;
  stakeholders: Stakeholder[];
  /** V8 — internal accountable stakeholders, shown on results + report cover. */
  internalStakeholders: InternalStakeholder[];
  logoDataUrl?: string;
  logoMime?: "image/png" | "image/svg+xml";
  watermarkEnabled: boolean;
  watermarkText: string;
  /** Industry weighting profile for the maturity-Stage calc. */
  industryProfile?: string;
};

export const DEFAULT_BRANDING: Branding = {
  orgName: "",
  stakeholders: [],
  internalStakeholders: [],
  logoDataUrl: undefined,
  logoMime: undefined,
  watermarkEnabled: false,
  watermarkText: "Internal Testing — Not for External Distribution",
  industryProfile: "default",
};

export type MultiAssessmentInput = {
  useCases: UseCaseSelection[]; // 1..5
  maturity: MaturityAnswers; // shared organization-wide
  techAdoption: TechAdoptionAnswers; // shared organization-wide
  /** Org-wide autonomy level (1..5), optional. Default 3 (neutral) if unset. */
  autonomyLevel?: AutonomyLevel;
};

export type ConsolidationMode = "max" | "weighted_avg";

export type PerUseCaseProfile = {
  useCaseId: UseCaseId;
  profile: RiskProfile;
};

export type ThreatCoverageRow = {
  threatId: string;
  perUseCase: Record<UseCaseId, ThreatExposure | null>; // null = threat not in this use case
};

export type MultiRiskProfile = {
  consolidationMode: ConsolidationMode;
  perUseCase: PerUseCaseProfile[];
  consolidated: RiskProfile; // built from union of threats
  threatCoverage: ThreatCoverageRow[];
  generatedAt: string;
};
